import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { User, Plus, X } from 'lucide-react'
import { client } from '@/api/client'
import { CoParentForm } from '@/components/CoParentForm'
import { useAuthStore } from '@/stores/auth'
import { Parent } from '@/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { UppercaseInput } from '@/components/ui/uppercase-input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'

interface PhoneData {
  id?: number
  number: string
  phone_type: string
}

interface EditProfileForm {
  first_name: string
  last_name: string
  street_name: string
  street_number: string
  city: string
  zip_code: string
  vat_number: string
  social_security_number: string
}

export function EditProfilePage() {
  const { t } = useTranslation(['home', 'common', 'profile'])
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { parent, updateParent } = useAuthStore()
  const [editError, setEditError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [phones, setPhones] = useState<PhoneData[]>([])
  const [showCoParentForm, setShowCoParentForm] = useState(false)
  const [highlightCoparentSsn, setHighlightCoparentSsn] = useState(false)

  useEffect(() => {
    const shouldHighlight = searchParams.get('highlightCoparentSsn') === 'true' || 
                           sessionStorage.getItem('highlightSSN') === 'true';
    const targetParentId = sessionStorage.getItem('targetParentId');
    if (shouldHighlight && targetParentId && parent?.co_parent?.id === parseInt(targetParentId)) {
      setHighlightCoparentSsn(true);
      sessionStorage.removeItem('highlightSSN');
      sessionStorage.removeItem('targetParentId');
    }
  }, [searchParams, parent?.co_parent?.id])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
  } = useForm<EditProfileForm>()

  useEffect(() => {
    if (parent) {
      setIsLoading(false)
      if (parent.co_parent) {
        verifyCoParentExists(parent.co_parent.id);
      }
    }
  }, [parent])

  const verifyCoParentExists = async (coParentId: number) => {
    try {
      await client.get(`/co-parents/${coParentId}/`);
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        if (parent) {
          updateParent({ ...parent, co_parent: null });
        }
      }
    }
  }

  useEffect(() => {
    if (!isLoading && parent) {
      reset({
        first_name: parent.first_name || '',
        last_name: parent.last_name || '',
        street_name: parent.street_name || '',
        street_number: parent.street_number || '',
        city: parent.city || '',
        zip_code: parent.zip_code || '',
        vat_number: parent.vat_number || '',
        social_security_number: parent.social_security_number || '',
      })
      if (parent.phones && parent.phones.length > 0) {
        setPhones(parent.phones)
      }
    }
  }, [isLoading, parent, reset])

  const addPhone = () => {
    if (phones.length < 5) {
      setPhones([...phones, { number: '', phone_type: 'mobile' }])
    } else {
      setEditError(t('validation.maxPhones', { ns: 'profile' }))
    }
  }

  const removePhone = (index: number) => {
    if (phones.length > 1) {
      setPhones(phones.filter((_, i) => i !== index))
    }
  }

  const updatePhoneType = (index: number, type: string) => {
    const newPhones = [...phones]
    newPhones[index].phone_type = type
    setPhones(newPhones)
  }

  const updatePhoneNumber = (index: number, number: string) => {
    const newPhones = [...phones]
    newPhones[index].number = number
    setPhones(newPhones)
  }

  const onSubmit = async (data: EditProfileForm) => {
    setIsSubmitting(true)
    setEditError(null)

    const profileData = {
      first_name: data.first_name,
      last_name: data.last_name,
      street_name: data.street_name,
      street_number: data.street_number,
      city: data.city,
      zip_code: data.zip_code,
      vat_number: data.vat_number,
      social_security_number: data.social_security_number,
      phones: phones
    }

    try {
      const hasMobilePhone = phones.some(phone => phone.phone_type === 'mobile')
      if (!hasMobilePhone) {
        setEditError(t('validation.mobileRequired', { ns: 'profile' }))
        setIsSubmitting(false)
        return
      }
      if (phones.length > 5) {
        setEditError(t('validation.maxPhones', { ns: 'profile' }))
        setIsSubmitting(false)
        return
      }
      if (phones.some(phone => !phone.number || phone.number.trim() === '')) {
        setEditError(t('validation.phoneNumberEmpty', { ns: 'profile' }))
        setIsSubmitting(false)
        return
      }

      const response = await client.patch('/profile/', profileData)
      updateParent(response.data)
      navigate('/')
    } catch (error: unknown) {
      const err = error as any;
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        if (errorData.vat_number) {
          if (errorData.vat_number[0].includes('9 digits')) {
            setError('vat_number', { type: 'manual', message: t('validation.vatFormat', { ns: 'profile' }) });
          } else if (errorData.vat_number[0].includes('check digit')) {
            setError('vat_number', { type: 'manual', message: t('validation.vatInvalid', { ns: 'profile' }) });
          } else if (errorData.vat_number[0].includes('already in use')) {
            setError('vat_number', { type: 'manual', message: t('validation.vatExists', { ns: 'profile' }) });
          }
        }
        if (errorData.social_security_number) {
          if (errorData.social_security_number[0].includes('11 digits')) {
            setError('social_security_number', { type: 'manual', message: t('validation.ssnFormat', { ns: 'profile' }) });
          } else if (errorData.social_security_number[0].includes('already in use')) {
            setError('social_security_number', { type: 'manual', message: t('validation.ssnExists', { ns: 'profile' }) });
          }
        }
        setEditError(t('errors.updateFailed', { ns: 'profile' }))
      } else {
        setEditError(t('errors.updateFailed', { ns: 'profile' }))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => navigate('/')

  const handleAddCoParent = () => setShowCoParentForm(true)

  const handleCoParentSuccess = (newCoParent: Parent) => {
    setShowCoParentForm(false)
    if (parent) {
      updateParent({ ...parent, co_parent: newCoParent })
    }
  }

  const handleCoParentRemove = () => {
    if (parent) {
      updateParent({ ...parent, co_parent: null })
    }
  }

  const isPhoneError = editError === t('validation.mobileRequired', { ns: 'profile' }) ||
                       editError === t('validation.maxPhones', { ns: 'profile' }) ||
                       editError === t('validation.phoneNumberEmpty', { ns: 'profile' })

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {t('profile.edit.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {editError && !isPhoneError && (
            <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3 mb-4">
              <p className="text-sm text-destructive">{editError}</p>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">
                    {t('profile.edit.firstName', { ns: 'profile' })}
                    <span className="text-muted-foreground text-xs italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                  </Label>
                  <UppercaseInput id="first_name" {...register('first_name', { required: t('profile.edit.firstNameRequired', { ns: 'profile' }) })} />
                  {errors.first_name && <p className="text-xs text-destructive">{errors.first_name.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">
                    {t('profile.edit.lastName', { ns: 'profile' })}
                    <span className="text-muted-foreground text-xs italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                  </Label>
                  <UppercaseInput id="last_name" {...register('last_name', { required: t('profile.edit.lastNameRequired', { ns: 'profile' }) })} />
                  {errors.last_name && <p className="text-xs text-destructive">{errors.last_name.message}</p>}
                </div>
              </div>

              {/* Email (readonly) */}
              <div className="space-y-2">
                <Label htmlFor="email">
                  {t('profile.edit.email', { ns: 'profile' })}
                  <span className="text-muted-foreground text-xs italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                </Label>
                <Input id="email" type="email" value={parent?.email || ''} disabled className="bg-muted" />
              </div>

              {/* SSN & VAT */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="social_security_number">
                    {t('fields.socialSecurityNumber', { ns: 'profile' })}
                    <span className="text-muted-foreground text-xs italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                  </Label>
                  <Input
                    id="social_security_number"
                    {...register('social_security_number', {
                      required: parent?.skip_checks ? false : t('validation.ssnRequired', { ns: 'profile' }),
                      pattern: { value: /^\d{11}$/, message: t('validation.ssnFormat', { ns: 'profile' }) }
                    })}
                    placeholder={t('fields.socialSecurityNumberPlaceholder', { ns: 'profile' })}
                  />
                  {errors.social_security_number && <p className="text-xs text-destructive">{errors.social_security_number.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vat_number">
                    {t('fields.vatNumber', { ns: 'profile' })}
                    <span className="text-muted-foreground text-xs italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                  </Label>
                  <Input
                    id="vat_number"
                    {...register('vat_number', {
                      required: parent?.skip_checks ? false : t('validation.vatRequired', { ns: 'profile' }),
                      pattern: { value: /^\d{9}$/, message: t('validation.vatFormat', { ns: 'profile' }) }
                    })}
                    placeholder={t('fields.vatNumberPlaceholder', { ns: 'profile' })}
                  />
                  {errors.vat_number && <p className="text-xs text-destructive">{errors.vat_number.message}</p>}
                </div>
              </div>

              <Separator />

              {/* Address */}
              <div>
                <h3 className="text-base font-semibold mb-3">
                  {t('profile.edit.addressSection', { ns: 'profile' })}
                  <span className="text-muted-foreground text-xs italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="street_name">{t('fields.streetName', { ns: 'profile' })}</Label>
                    <UppercaseInput id="street_name" {...register('street_name', { required: t('validation.streetNameRequired', { ns: 'profile' }) })} placeholder={t('fields.streetNamePlaceholder', { ns: 'profile' })} />
                    {errors.street_name && <p className="text-xs text-destructive">{errors.street_name.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="street_number">{t('fields.streetNumber', { ns: 'profile' })}</Label>
                    <UppercaseInput id="street_number" {...register('street_number', { required: t('validation.streetNumberRequired', { ns: 'profile' }) })} placeholder={t('fields.streetNumberPlaceholder', { ns: 'profile' })} />
                    {errors.street_number && <p className="text-xs text-destructive">{errors.street_number.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">{t('fields.city', { ns: 'profile' })}</Label>
                    <UppercaseInput id="city" {...register('city', { required: t('validation.cityRequired', { ns: 'profile' }) })} placeholder={t('fields.cityPlaceholder', { ns: 'profile' })} />
                    {errors.city && <p className="text-xs text-destructive">{errors.city.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip_code">{t('fields.zipCode', { ns: 'profile' })}</Label>
                    <Input id="zip_code" {...register('zip_code', { required: t('validation.zipCodeRequired', { ns: 'profile' }) })} placeholder={t('fields.zipCodePlaceholder', { ns: 'profile' })} />
                    {errors.zip_code && <p className="text-xs text-destructive">{errors.zip_code.message}</p>}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Phone Numbers */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>
                    {t('fields.phoneNumbers', { ns: 'profile' })}
                    <span className="text-muted-foreground text-xs italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                  </Label>
                  <Button type="button" variant="ghost" size="sm" onClick={addPhone} disabled={phones.length >= 5}>
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    {t('fields.addPhone', { ns: 'profile' })}
                  </Button>
                </div>
                {phones.map((phone, index) => (
                  <div key={index} className="flex gap-2">
                    <select
                      value={phone.phone_type}
                      onChange={(e) => updatePhoneType(index, e.target.value)}
                      className="flex h-9 w-[130px] rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <option value="mobile">{t('fields.mobile', { ns: 'profile' })}</option>
                      <option value="home">{t('fields.home', { ns: 'profile' })}</option>
                      <option value="work">{t('fields.work', { ns: 'profile' })}</option>
                      <option value="other">{t('fields.other', { ns: 'profile' })}</option>
                    </select>
                    <Input
                      type="text"
                      value={phone.number}
                      onChange={(e) => updatePhoneNumber(index, e.target.value)}
                      placeholder={t('fields.phoneNumberPlaceholder', { ns: 'profile' })}
                      className={`flex-1 ${!phone.number || phone.number.trim() === '' ? 'border-destructive' : ''}`}
                      required
                    />
                    {phones.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removePhone(index)} className="flex-shrink-0">
                        <X className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
                {isPhoneError && <p className="text-xs text-destructive">{editError}</p>}
              </div>

              <Separator />

              {/* Co-Parent Section */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-base font-semibold">{t('coParent.section.title', { ns: 'profile' })}</h3>
                  {!parent?.co_parent && !showCoParentForm && (
                    <Button type="button" variant="outline" size="sm" onClick={handleAddCoParent}>
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      {t('coParent.actions.add', { ns: 'profile' })}
                    </Button>
                  )}
                </div>

                {!showCoParentForm && !parent?.co_parent && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm space-y-2">
                    <p>{t('coParent.info.description', { ns: 'profile' })}</p>
                    <p className="font-medium">{t('coParent.info.reminder', { ns: 'profile' })}</p>
                    <p className="font-medium text-amber-800">{t('coParent.info.important', { ns: 'profile' })}</p>
                  </div>
                )}

                {showCoParentForm && (
                  <CoParentForm
                    onSuccess={handleCoParentSuccess}
                    onRemove={handleCoParentRemove}
                    existingCoParent={parent?.co_parent || null}
                    parent={parent}
                    isNested={true}
                    highlightSsn={highlightCoparentSsn}
                  />
                )}

                {parent?.co_parent && !showCoParentForm && (
                  <CoParentForm
                    existingCoParent={parent.co_parent}
                    onSuccess={handleCoParentSuccess}
                    onRemove={handleCoParentRemove}
                    isNested={true}
                    parent={parent}
                    highlightSsn={highlightCoparentSsn}
                  />
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={handleCancel}>
                  {t('common:buttons.cancel')}
                </Button>
                <Button type="submit" className="flex-1" disabled={isSubmitting}>
                  {isSubmitting ? t('common:buttons.saving') : t('common:buttons.save')}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
